import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getHookahLogsWithCount, getUserByQrToken } from '../../services/publicUserApi';
import { getShopItems } from '../../services/loyaltyShopApi';
import { getHookahTypes } from '../../services/hookahTypeApi';
import { logoutLoyalty } from '../../services/loyaltyAuthApi';
import { loyaltySession } from '../../utils/session';
import { restoreLoyaltySession } from '../../utils/loyaltySessionRestore';
import PromotedShopModal from '../../components/PromotedShopModal/PromotedShopModal';
import { usePublicFeatureFlag } from '../../hooks/usePublicFeatureFlag';
import { FEATURE_FLAGS } from '../../constants/featureFlags';
import './LoyaltyProfile.css';

function LoyaltyProfile() {
    const { t, i18n } = useTranslation();
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const { enabled: loyaltyReservationsEnabled } = usePublicFeatureFlag(FEATURE_FLAGS.LOYALTY_USER_RESERVATIONS);
    const { token: qrToken } = useParams(); // Get QR token from URL if present
    const location = useLocation();
    const [user, setUser] = useState(null);
    const [hookahCount, setHookahCount] = useState(null);
    const [recentLogs, setRecentLogs] = useState([]);
    const [recentPurchases, setRecentPurchases] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [isQrScan, setIsQrScan] = useState(false); // Track if viewing via QR scan
    const [showShop, setShowShop] = useState(false); // Toggle shop view
    const [shopItems, setShopItems] = useState([]);
    const [shopLoading, setShopLoading] = useState(false);
    const [shopError, setShopError] = useState(null);
    const [breakdownDialogOpen, setBreakdownDialogOpen] = useState(false);
    const [countdown, setCountdown] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [hookahTypes, setHookahTypes] = useState([]);
    const [showPromotedModal, setShowPromotedModal] = useState(false);
    const navigate = useNavigate();

    const handleLogout = () => {
        logoutLoyalty();
        navigate('/loyalty/login', { replace: true });
    };

    const handleToggleShop = async () => {
        if (!showShop) {
            // Fetch shop items when opening shop
            if (shopItems.length === 0) {
                try {
                    setShopLoading(true);
                    const data = await getShopItems();
                    setShopItems(data);
                    setShopError(null);
                } catch (err) {
                    console.error('Error fetching shop items:', err);
                    setShopError(err.message);
                } finally {
                    setShopLoading(false);
                }
            }
            setShowShop(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const getItemName = (item) => {
        return i18n.language === 'bg' && item.name_bg ? item.name_bg : item.name;
    };

    const getItemDescription = (item) => {
        return i18n.language === 'bg' && item.description_bg ? item.description_bg : item.description;
    };

    const canAfford = (cost) => {
        const balance = summary ? summary.current_balance : hookahCount;
        return balance >= cost;
    };

    useEffect(() => {
        const fetchData = async () => {
            const storedUser = await restoreLoyaltySession({ forceValidate: true });

            if (!storedUser) {
                navigate('/loyalty/login');
                return;
            }

            let loggedInUser = storedUser;

            if (loggedInUser.preferred_language && i18n.language !== loggedInUser.preferred_language) {
                i18n.changeLanguage(loggedInUser.preferred_language);
            }

            // Check if accessing via QR token in URL
            if (qrToken) {
                // Verify that the QR token matches the logged in user's token
                if (loggedInUser.qr_token !== qrToken) {
                    // QR token doesn't match logged in user - show error and redirect
                    setError('Не можете да достъпите профила на друг потребител. Моля, влезте със своя акаунт.');
                    setCountdown(8);
                    setLoading(false);
                    setTimeout(() => {
                        navigate('/loyalty/login');
                    }, 8000);
                    return;
                }

                // Token matches - load user data via QR token
                try {
                    setIsQrScan(true);
                    const data = await getUserByQrToken(qrToken);

                    const userData = {
                        id: data.id,
                        name: data.name || data.user?.name || 'Loyalty Member',
                        email: data.email || data.user?.email,
                        phone: data.phone || data.user?.phone,
                        qr_token: qrToken,
                        qr_code_url: data.qr_code_url,
                        hookahs_count: data.hookahs_count || 0
                    };

                    setUser(userData);
                    setHookahCount(data.hookahs_count || 0);
                    setRecentLogs(data.recent_logs || []);
                    setRecentPurchases(data.recent_purchases || []);
                    setSummary(data.summary || null);
                    setLoading(false);
                    return;
                } catch (err) {
                    console.error('Failed to fetch QR user data:', err);

                    // Check if user was not found (deleted or doesn't exist)
                    if (err.message && (err.message.includes('not found') || err.message.includes('404') || err.message.includes('No query results for model'))) {
                        // Clear session and redirect to login
                        loyaltySession.clear();
                        navigate('/loyalty/login', {
                            state: { message: t('loyalty.profile.errors.profileNotFound') }
                        });
                        return;
                    }

                    setError(t('loyalty.profile.errors.loadUserData'));
                    setLoading(false);
                    return;
                }
            }

            // Normal logged-in user flow (no QR token in URL)
            try {
                setUser(loggedInUser);

                // Fetch fresh hookah count and logs from server
                if (loggedInUser.id) {
                    try {
                        const logsData = await getHookahLogsWithCount(loggedInUser.id);
                        setHookahCount(logsData.hookahs_count || 0);
                        setRecentLogs(logsData.recent_logs || []);
                        setRecentPurchases(logsData.recent_purchases || []);
                        setSummary(logsData.summary || null);
                    } catch (fetchErr) {
                        console.error('Failed to fetch hookah data:', fetchErr);

                        // Check if user was not found (deleted or doesn't exist)
                        if (fetchErr.message && (fetchErr.message.includes('not found') || fetchErr.message.includes('404') || fetchErr.message.includes('No query results for model'))) {
                            // Clear session and redirect to login
                            loyaltySession.clear();
                            navigate('/loyalty/login', {
                                state: { message: t('loyalty.profile.errors.profileNotFound') }
                            });
                            return;
                        }

                        // Fallback to stored count
                        setHookahCount(loggedInUser.hookahs_count || 0);
                        setRecentLogs([]);
                    }
                } else {
                    setHookahCount(loggedInUser.hookahs_count || 0);
                    setRecentLogs([]);
                }

                setLoading(false);
            } catch (err) {
                console.error('Failed to load user data:', err);
                setError(t('loyalty.profile.errors.loadUserData'));
                setLoading(false);
            }
        };

        fetchData();
    }, [navigate, t, qrToken]);

    // Show promoted shop modal after login (only for regular logged-in users, not QR scans)
    useEffect(() => {
        if (!loading && user && !isQrScan) {
            // Check if modal is blocked for 1 week due to 3 consecutive X closes
            const blockedUntil = localStorage.getItem('promoted_modal_blocked_until');
            if (blockedUntil) {
                const blockedDate = new Date(blockedUntil);
                const now = new Date();
                if (now < blockedDate) {
                    console.log('Modal blocked until:', blockedDate);
                    return; // Don't show modal
                } else {
                    // Block period expired, clear it
                    localStorage.removeItem('promoted_modal_blocked_until');
                    localStorage.removeItem('promoted_modal_x_closes');
                }
            }

            // Check if 24 hours have passed since last seen
            const lastSeen = localStorage.getItem('promoted_shop_last_seen');
            if (!lastSeen) {
                // First time, show modal
                setShowPromotedModal(true);
            } else {
                const lastSeenDate = new Date(lastSeen);
                const now = new Date();
                const hoursSinceLastSeen = (now - lastSeenDate) / (1000 * 60 * 60);

                if (hoursSinceLastSeen >= 24) {
                    setShowPromotedModal(true);
                }
            }
        }
    }, [loading, user, isQrScan]);

    // Fetch hookah types for legend
    useEffect(() => {
        const fetchHookahTypes = async () => {
            try {
                const types = await getHookahTypes();
                // Filter only active types and sort by sort_order
                const activeTypes = types
                    .filter(type => type.is_active)
                    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
                setHookahTypes(activeTypes);
            } catch (err) {
                console.error('Failed to fetch hookah types:', err);
            }
        };
        fetchHookahTypes();
    }, []);

    // Countdown timer effect
    useEffect(() => {
        if (countdown !== null && countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    if (loading) {
        return (
            <Box className="loyalty-profile-container">
                <CircularProgress sx={{ color: '#ff6b00' }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box className="loyalty-profile-container" sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                padding: 3
            }}>
                <Card sx={{
                    maxWidth: 500,
                    width: '100%',
                    bgcolor: 'rgba(244, 67, 54, 0.1)',
                    border: '2px solid #f44336',
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(244, 67, 54, 0.3)'
                }}>
                    <CardContent sx={{ textAlign: 'center', p: 4 }}>
                        <Box sx={{
                            fontSize: '4rem',
                            mb: 2,
                            animation: 'shake 0.5s ease-in-out'
                        }}>
                            🚫
                        </Box>
                        <Typography variant="h5" sx={{
                            color: '#f44336',
                            fontWeight: 700,
                            mb: 2
                        }}>
                            Достъп отказан
                        </Typography>
                        <Alert severity="error" sx={{
                            mb: 2,
                            '& .MuiAlert-message': {
                                fontSize: '1rem'
                            }
                        }}>
                            {error}
                        </Alert>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            mb: 2
                        }}>
                            <Typography variant="body2" sx={{
                                color: 'rgba(0,0,0,0.6)'
                            }}>
                                Ще бъдете препратени след:
                            </Typography>
                            <Box sx={{
                                position: 'relative',
                                display: 'inline-flex'
                            }}>
                                <CircularProgress
                                    variant="determinate"
                                    value={(countdown / 8) * 100}
                                    size={80}
                                    thickness={4}
                                    sx={{
                                        color: '#f44336',
                                        transform: 'rotate(-90deg) !important'
                                    }}
                                />
                                <Box sx={{
                                    top: 0,
                                    left: 0,
                                    bottom: 0,
                                    right: 0,
                                    position: 'absolute',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Typography
                                        variant="h4"
                                        component="div"
                                        sx={{
                                            color: '#f44336',
                                            fontWeight: 700
                                        }}
                                    >
                                        {countdown}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2" sx={{
                                color: 'rgba(0,0,0,0.6)'
                            }}>
                                {countdown === 1 ? 'секунда' : 'секунди'}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
                <style>
                    {`
                        @keyframes shake {
                            0%, 100% { transform: translateX(0); }
                            25% { transform: translateX(-10px); }
                            75% { transform: translateX(10px); }
                        }
                    `}
                </style>
            </Box>
        );
    }

    if (!user) {
        return null;
    }

    // Get initials for avatar
    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.split(' ');
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return name[0].toUpperCase();
    };

    // Determine loyalty tier based on hookahs smoked
    const getLoyaltyTier = (count) => {
        if (count >= 200) return { name: t('loyalty.profile.tiers.platinum'), color: '#e5e4e2' };
        if (count >= 100) return { name: t('loyalty.profile.tiers.gold'), color: '#ffd700' };
        if (count >= 50) return { name: t('loyalty.profile.tiers.silver'), color: '#c0c0c0' };
        return { name: t('loyalty.profile.tiers.bronze'), color: '#cd7f32' };
    };

    // Get next tier progress message
    const getNextTierMessage = (count) => {
        if (count >= 200) {
            return t('loyalty.profile.tiers.maxReached');
        } else if (count >= 100) {
            return t('loyalty.profile.tiers.moreToPlatinum', { count: 200 - count });
        } else if (count >= 50) {
            return t('loyalty.profile.tiers.moreToGold', { count: 100 - count });
        } else {
            return t('loyalty.profile.tiers.moreToSilver', { count: 50 - count });
        }
    };

    const tier = getLoyaltyTier(hookahCount);

    return (
        <Box className="loyalty-profile-container">
            <Card className="loyalty-profile-card">
                <CardContent>
                    {/* Shop View */}
                    {showShop && !isQrScan ? (
                        <>
                            {/* Shop Header with Back Button */}
                            <Box sx={{ mb: 3 }}>
                                <Button
                                    startIcon={<ArrowBackIcon />}
                                    onClick={() => {
                                        setShowShop(false);
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                    sx={{
                                        color: '#ff6b00',
                                        mb: 2,
                                        '&:hover': {
                                            bgcolor: 'rgba(255, 107, 0, 0.1)'
                                        }
                                    }}
                                >
                                    {t('loyalty.profile.backToProfile', 'Back to Profile')}
                                </Button>
                                <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, textAlign: 'center' }}>
                                    🏪 {t('shop.title', 'Loyalty Shop')}
                                </Typography>
                            </Box>

                            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3, textAlign: 'center' }}>
                                {t('shop.subtitle', 'Redeem your hookahs for amazing rewards!')}
                            </Typography>

                            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                                <Chip
                                    label={`${t('loyalty.profile.yourPoints', 'Your Points')}: ${summary ? summary.current_balance : hookahCount}`}
                                    sx={{
                                        bgcolor: '#ff6b00',
                                        color: '#fff',
                                        fontWeight: 700,
                                        fontSize: '1.1rem',
                                        px: 2,
                                        py: 2.5
                                    }}
                                />
                            </Box>

                            <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                            {/* Purchase History */}
                            {recentPurchases.length > 0 && (
                                <>
                                    <Typography variant="h6" sx={{ color: '#ff6b00', fontWeight: 600, mb: 2 }}>
                                        🎁 {t('shop.yourRewards', 'Your Rewards')}
                                    </Typography>
                                    <Box sx={{ mb: 3, maxHeight: 300, overflowY: 'auto' }}>
                                        {recentPurchases.map((purchase) => (
                                            <Card
                                                key={purchase.id}
                                                sx={{
                                                    mb: 2,
                                                    bgcolor: 'rgba(76, 175, 80, 0.1)',
                                                    border: '1px solid rgba(76, 175, 80, 0.3)'
                                                }}
                                            >
                                                <CardContent>
                                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                        {purchase.shop_item?.image_url && (
                                                            <Box
                                                                component="img"
                                                                src={purchase.shop_item.image_url}
                                                                alt={getItemName(purchase.shop_item)}
                                                                sx={{
                                                                    width: 60,
                                                                    height: 60,
                                                                    objectFit: 'cover',
                                                                    borderRadius: 1
                                                                }}
                                                            />
                                                        )}
                                                        <Box sx={{ flexGrow: 1 }}>
                                                            <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 600 }}>
                                                                {purchase.shop_item ? getItemName(purchase.shop_item) : t('shop.deletedItem', 'Deleted Item')}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block' }}>
                                                                {new Date(purchase.created_at).toLocaleDateString(i18n.language === 'bg' ? 'bg-BG' : 'en-US', {
                                                                    year: 'numeric',
                                                                    month: 'short',
                                                                    day: 'numeric'
                                                                })}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                                                                <Chip
                                                                    label={`-${purchase.hookah_cost} ${t('loyalty.profile.points', 'points')}`}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: '#f44336',
                                                                        color: '#fff',
                                                                        fontWeight: 600,
                                                                        fontSize: '0.7rem'
                                                                    }}
                                                                />
                                                                {purchase.status === 'completed' && (
                                                                    <Chip
                                                                        label={`✓ ${t('loyalty.profile.received', 'Received')}`}
                                                                        size="small"
                                                                        sx={{
                                                                            bgcolor: '#4caf50',
                                                                            color: '#fff',
                                                                            fontSize: '0.7rem'
                                                                        }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        ))}
                                    </Box>
                                    <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                                </>
                            )}

                            {/* Shop Items */}
                            <Typography variant="h6" sx={{ color: '#ff6b00', fontWeight: 600, mb: 2 }}>
                                🏪 {t('shop.availableRewards', 'Available Rewards')}
                            </Typography>
                            {shopLoading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress sx={{ color: '#ff6b00' }} />
                                </Box>
                            )}

                            {shopError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {shopError}
                                </Alert>
                            )}

                            {!shopLoading && !shopError && shopItems.length === 0 && (
                                <Alert severity="info">
                                    {t('shop.noItems', 'No items available at the moment. Check back soon!')}
                                </Alert>
                            )}

                            {!shopLoading && !shopError && shopItems.length > 0 && (
                                <Grid container spacing={2}>
                                    {shopItems.filter(item => item.is_active).map(item => (
                                        <Grid item xs={12} sm={6} key={item.id} sx={{ display: 'flex' }}>
                                            <Card
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    minHeight: '350px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    position: 'relative',
                                                    bgcolor: canAfford(item.hookah_cost) ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                                                    border: canAfford(item.hookah_cost) ? '1px solid rgba(76, 175, 80, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
                                                    transition: 'all 0.2s',
                                                    opacity: canAfford(item.hookah_cost) ? 1 : 0.6,
                                                    '&:hover': canAfford(item.hookah_cost) ? {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                                                    } : {}
                                                }}
                                            >
                                                {/* Stock Badge */}
                                                {item.remaining_stock === null ? (
                                                    <Chip
                                                        label={`♾️ ${t('loyalty.profile.unlimited', 'Unlimited')}`}
                                                        size="small"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 30,
                                                            right: 30,
                                                            bgcolor: '#4caf50',
                                                            color: '#fff',
                                                            fontWeight: 700,
                                                            zIndex: 1,
                                                            fontSize: '0.85rem'
                                                        }}
                                                    />
                                                ) : item.remaining_stock === 0 ? (
                                                    <Chip
                                                        label={`❌ ${t('loyalty.profile.soldOut', 'Sold Out')}`}
                                                        size="small"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 30,
                                                            right: 30,
                                                            bgcolor: '#f44336',
                                                            color: '#fff',
                                                            fontWeight: 700,
                                                            zIndex: 1,
                                                            fontSize: '0.85rem'
                                                        }}
                                                    />
                                                ) : item.remaining_stock > 0 && (
                                                    <Chip
                                                        label={t('loyalty.profile.itemsLeft', '{{count}} left', { count: item.remaining_stock })}
                                                        size="small"
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 30,
                                                            right: 30,
                                                            bgcolor: item.remaining_stock <= 10 ? '#ffb300' : '#4caf50',
                                                            color: item.remaining_stock <= 10 ? '#000' : '#fff',
                                                            fontWeight: 700,
                                                            zIndex: 1,
                                                            fontSize: '0.85rem'
                                                        }}
                                                    />
                                                )}
                                                {item.image_url && (
                                                    <Box
                                                        component="img"
                                                        src={item.image_url}
                                                        alt={getItemName(item)}
                                                        onClick={() => setImagePreview({ url: item.image_url, name: getItemName(item) })}
                                                        sx={{
                                                            width: '100%',
                                                            height: { xs: 220, sm: 200 },
                                                            objectFit: 'cover',
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                            padding: '0.5rem',
                                                            cursor: 'pointer',
                                                            transition: 'transform 0.2s',
                                                            '&:hover': {
                                                                transform: 'scale(1.05)'
                                                            }
                                                        }}
                                                    />
                                                )}
                                                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                                    <Box>
                                                        <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600, mb: 1, height: '28px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {getItemName(item)}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1.5, height: '40px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                            {getItemDescription(item)}
                                                        </Typography>
                                                    </Box>

                                                    <Box>
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1 }}>
                                                            <Chip
                                                                label={`${item.hookah_cost} ${t('loyalty.profile.points', 'points')}`}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: canAfford(item.hookah_cost) ? '#4caf50' : '#666',
                                                                    color: '#fff',
                                                                    fontWeight: 600
                                                                }}
                                                            />
                                                        </Box>

                                                        {!canAfford(item.hookah_cost) && (
                                                            <Typography variant="caption" sx={{ color: '#ff6b00', display: 'block', textAlign: 'center' }}>
                                                                {t('shop.needMore', 'Need {{count}} more points', { count: (item.hookah_cost - (summary ? summary.current_balance : hookahCount)).toFixed(1) })}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </>
                    ) : (
                        // Profile View
                        <>
                            {/* Header with Avatar and QR Code */}
                            <Box className="profile-header-grid">
                                <Box className="profile-info-left">
                                    <Typography variant="h5" className="profile-name" sx={{ mb: 0.5, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                                        {user.name}
                                    </Typography>
                                    <Chip
                                        label={`${tier.name} ${t('loyalty.profile.member')}`}
                                        size="small"
                                        sx={{
                                            bgcolor: tier.color,
                                            color: '#000',
                                            fontWeight: 'bold'
                                        }}
                                    />
                                </Box>

                                {/* Right: QR Code */}
                                {user.qr_code_url && (
                                    <Box className="qr-code-compact">
                                        <Typography variant="caption" sx={{ color: '#ff6b00', fontWeight: 600, display: 'block', mb: 0.5 }}>
                                            {t('loyalty.profile.loyaltyQrCode')}
                                        </Typography>
                                        <Box
                                            onClick={() => setQrModalOpen(true)}
                                            sx={{
                                                bgcolor: '#fff',
                                                borderRadius: '8px',
                                                padding: '0.5rem',
                                                width: { xs: '90px', sm: '120px' },
                                                height: { xs: '90px', sm: '120px' },
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                '&:hover': {
                                                    transform: 'scale(1.05)',
                                                    boxShadow: '0 4px 12px rgba(255, 107, 0, 0.3)'
                                                }
                                            }}
                                        >
                                            <img
                                                src={user.qr_code_url}
                                                alt="QR Code"
                                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                            />
                                        </Box>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                                            {t('loyalty.profile.clickToEnlarge')}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                            {/* Stats Grid */}
                            {summary && (
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
                                    gap: { xs: 1, sm: 1.5 },
                                    mb: 2
                                }}>
                                    {/* Current Balance */}
                                    <Box sx={{
                                        bgcolor: 'rgba(255, 107, 0, 0.1)',
                                        borderRadius: 2,
                                        p: { xs: 1, sm: 1.5 },
                                        border: '1px solid rgba(255, 107, 0, 0.3)',
                                        textAlign: 'center'
                                    }}>
                                        <Typography variant="h5" sx={{ color: '#ff6b00', fontWeight: 700, mb: 0.3 }}>
                                            {summary.current_balance}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.7rem' }}>
                                            {t('loyalty.profile.currentBalance', 'Current Balance')}
                                        </Typography>
                                    </Box>

                                    {/* Total Earned */}
                                    <Box sx={{
                                        bgcolor: 'rgba(76, 175, 80, 0.1)',
                                        borderRadius: 2,
                                        p: { xs: 1, sm: 1.5 },
                                        border: '1px solid rgba(76, 175, 80, 0.3)',
                                        textAlign: 'center'
                                    }}>
                                        <Typography variant="h5" sx={{ color: '#4caf50', fontWeight: 700, mb: 0.3 }}>
                                            💰 {summary.total_points_earned}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.7rem' }}>
                                            {t('loyalty.profile.totalEarned', 'Total Earned')}
                                        </Typography>
                                    </Box>

                                    {/* Total Hookahs */}
                                    <Box sx={{
                                        bgcolor: 'rgba(255, 107, 0, 0.1)',
                                        borderRadius: 2,
                                        p: { xs: 1, sm: 1.5 },
                                        border: '1px solid rgba(255, 107, 0, 0.3)',
                                        textAlign: 'center'
                                    }}>
                                        <Typography variant="h5" sx={{ color: '#ff6b00', fontWeight: 700, mb: 0.3 }}>
                                            🔥 {summary.total_hookahs_consumed}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.7rem' }}>
                                            {t('loyalty.profile.hookahsConsumed', 'Hookahs')}
                                        </Typography>
                                    </Box>

                                    {/* Total Spent - Full width if exists */}
                                    {summary.total_points_spent > 0 && (
                                        <Box sx={{
                                            bgcolor: 'rgba(255, 152, 0, 0.1)',
                                            borderRadius: 2,
                                            p: { xs: 1, sm: 1.5 },
                                            border: '1px solid rgba(255, 152, 0, 0.3)',
                                            textAlign: 'center',
                                            gridColumn: { xs: 'span 2', sm: 'span 3' }
                                        }}>
                                            <Typography variant="h5" sx={{ color: '#ff9800', fontWeight: 700, mb: 0.3 }}>
                                                🎁 {summary.total_points_spent}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.7rem' }}>
                                                {t('loyalty.profile.totalSpent', 'Total Spent')}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Hookah Points - Full width */}
                                    {hookahTypes.length > 0 && (
                                        <Box sx={{
                                            gridColumn: { xs: 'span 2', sm: 'span 3' },
                                            p: { xs: 1, sm: 1.5 },
                                            bgcolor: 'rgba(255, 107, 0, 0.05)',
                                            borderRadius: 2,
                                            border: '1px solid rgba(255, 107, 0, 0.2)'
                                        }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    mb: 1,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    fontSize: '0.65rem',
                                                    fontWeight: 600
                                                }}
                                            >
                                                🎯 {t('loyalty.profile.pointsPerType', 'Точки по тип наргиле')}
                                            </Typography>
                                            <Box sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 0.75
                                            }}>
                                                {hookahTypes.map((type) => (
                                                    <Chip
                                                        key={type.id}
                                                        label={`${i18n.language === 'bg' && type.name_bg ? type.name_bg : type.name}: ${Number(type.points).toFixed(1).replace(/\.0$/, '')}${t('loyalty.profile.pts', 'т')}`}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: 'rgba(255, 107, 0, 0.15)',
                                                            color: '#fff',
                                                            fontWeight: 500,
                                                            fontSize: '0.7rem',
                                                            height: '22px',
                                                            border: '1px solid rgba(255, 107, 0, 0.3)',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(255, 107, 0, 0.25)'
                                                            }
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {!summary && (
                                <Box sx={{ mb: 2 }}>
                                    <Box sx={{
                                        bgcolor: 'rgba(255, 107, 0, 0.1)',
                                        borderRadius: 2,
                                        p: { xs: 1.5, sm: 2 },
                                        border: '1px solid rgba(255, 107, 0, 0.3)',
                                        textAlign: 'center'
                                    }}>
                                        <Typography variant="h3" sx={{ color: '#ff6b00', fontWeight: 700, mb: 0.5, fontSize: { xs: '2rem', sm: '3rem' } }}>
                                            {hookahCount}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                                            {t('loyalty.profile.hookahsSmoked')}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#ff6b00', display: 'block', fontSize: '0.8rem', fontWeight: 600 }}>
                                            {getNextTierMessage(hookahCount)}
                                        </Typography>
                                    </Box>

                                    {/* Hookah Points for non-summary */}
                                    {hookahTypes.length > 0 && (
                                        <Box sx={{
                                            mt: 1.5,
                                            p: 1.5,
                                            bgcolor: 'rgba(255, 107, 0, 0.05)',
                                            borderRadius: 2,
                                            border: '1px solid rgba(255, 107, 0, 0.2)'
                                        }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    mb: 1,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    fontSize: '0.65rem',
                                                    fontWeight: 600
                                                }}
                                            >
                                                🎯 {t('loyalty.profile.pointsPerType', 'Точки по тип наргиле')}
                                            </Typography>
                                            <Box sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 0.75
                                            }}>
                                                {hookahTypes.map((type) => (
                                                    <Chip
                                                        key={type.id}
                                                        label={`${i18n.language === 'bg' && type.name_bg ? type.name_bg : type.name}: ${Number(type.points).toFixed(1).replace(/\.0$/, '')}${t('loyalty.profile.pts', 'т')}`}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: 'rgba(255, 107, 0, 0.15)',
                                                            color: '#fff',
                                                            fontWeight: 500,
                                                            fontSize: '0.7rem',
                                                            height: '22px',
                                                            border: '1px solid rgba(255, 107, 0, 0.3)',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(255, 107, 0, 0.25)'
                                                            }
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                            {/* Type Breakdown & Activity Button */}
                            {(summary && summary.by_type && summary.by_type.length > 0) || recentLogs.length > 0 ? (
                                <Box sx={{ mb: 2 }}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={() => setBreakdownDialogOpen(true)}
                                        sx={{
                                            color: '#ff6b00',
                                            borderColor: '#ff6b00',
                                            '&:hover': {
                                                borderColor: '#ff8c33',
                                                bgcolor: 'rgba(255, 107, 0, 0.1)'
                                            }
                                        }}
                                    >
                                        📊 {t('loyalty.profile.viewDetails', 'Виж Детайли и Активност')}
                                    </Button>
                                </Box>
                            ) : null}

                            <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                            {/* Action Buttons - Only show for logged-in users, not QR scans */}
                            {!isQrScan && (
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'column' }, gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        startIcon={<StorefrontIcon />}
                                        onClick={handleToggleShop}
                                        sx={{
                                            color: '#ff6b00',
                                            borderColor: '#ff6b00',
                                            '&:hover': {
                                                borderColor: '#ff8c33',
                                                bgcolor: 'rgba(255, 107, 0, 0.1)'
                                            }
                                        }}
                                    >
                                        {t('shop.title', 'Loyalty Shop')}
                                    </Button>
                                    {loyaltyReservationsEnabled && (
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        onClick={() => {
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                            navigate('/loyalty/reservations');
                                        }}
                                        sx={{
                                            color: '#ff6b00',
                                            borderColor: '#ff6b00',
                                            '&:hover': {
                                                borderColor: '#ff8c33',
                                                bgcolor: 'rgba(255, 107, 0, 0.1)'
                                            }
                                        }}
                                    >
                                        {t('loyalty.reservations.title', 'PlayStation Reservation')}
                                    </Button>
                                    )}
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        startIcon={<SettingsIcon />}
                                        onClick={() => {
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                            navigate('/loyalty/settings');
                                        }}
                                        sx={{
                                            color: '#ff6b00',
                                            borderColor: '#ff6b00',
                                            '&:hover': {
                                                borderColor: '#ff8c33',
                                                bgcolor: 'rgba(255, 107, 0, 0.1)'
                                            }
                                        }}
                                    >
                                        {t('loyalty.profile.settings')}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        startIcon={<LogoutIcon />}
                                        onClick={handleLogout}
                                        sx={{
                                            color: '#ff6b00',
                                            borderColor: '#ff6b00',
                                            '&:hover': {
                                                borderColor: '#ff8c33',
                                                bgcolor: 'rgba(255, 107, 0, 0.1)'
                                            }
                                        }}
                                    >
                                        {t('loyalty.profile.logout')}
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            {/* QR Code Modal */}
            <Dialog
                open={qrModalOpen}
                onClose={() => setQrModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: 'rgba(30, 30, 30, 0.98)',
                        borderRadius: '16px'
                    }
                }}
            >
                <DialogTitle sx={{
                    color: '#ff6b00',
                    fontWeight: 600,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(255, 107, 0, 0.2)'
                }}>
                    {t('loyalty.profile.qrModalTitle')}
                    <IconButton
                        onClick={() => setQrModalOpen(false)}
                        sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '2rem',
                    textAlign: 'center'
                }}>
                    <Box sx={{
                        bgcolor: '#fff',
                        borderRadius: '12px',
                        padding: '1.5rem',
                        width: '100%',
                        maxWidth: '350px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
                    }}>
                        <img
                            src={user?.qr_code_url}
                            alt="QR Code"
                            style={{
                                width: '100%',
                                height: 'auto',
                                maxWidth: '300px'
                            }}
                        />
                    </Box>
                    <Typography variant="body1" sx={{ color: '#fff', mt: 2, fontWeight: 500 }}>
                        {t('loyalty.profile.qrModalDescription')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 1 }}>
                        {user?.name}
                    </Typography>
                </DialogContent>
            </Dialog>

            {/* Type Breakdown Dialog */}
            <Dialog
                open={breakdownDialogOpen}
                onClose={() => setBreakdownDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: '#1a1a1a',
                        backgroundImage: 'none',
                        border: '1px solid rgba(255, 107, 0, 0.3)',
                        borderRadius: 2
                    }
                }}
            >
                <DialogTitle sx={{
                    color: '#ff6b00',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    📊 {t('loyalty.profile.typeBreakdown', 'Breakdown by Type')}
                    <IconButton
                        onClick={() => setBreakdownDialogOpen(false)}
                        sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {summary && summary.by_type && summary.by_type.map((type, index) => {
                            const typeName = type.name === 'Default'
                                ? (i18n.language === 'bg' ? 'Стандартно' : 'Standard')
                                : (i18n.language === 'bg' && type.name_bg ? type.name_bg : type.name);
                            return (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        bgcolor: 'rgba(255,255,255,0.03)',
                                        borderRadius: 1,
                                        p: 2,
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.05)',
                                            borderColor: 'rgba(255, 107, 0, 0.3)'
                                        }
                                    }}
                                >
                                    <Box>
                                        <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600 }}>
                                            {typeName}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                                            {type.times_ordered} {type.times_ordered === 1 ? t('loyalty.profile.time', 'time') : t('loyalty.profile.times', 'times')}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 600 }}>
                                                {parseFloat(type.total_points).toFixed(1)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                                {t('loyalty.profile.points', 'points')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Recent Activity Section */}
                    {recentLogs.length > 0 && (
                        <>
                            <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                            <Box>
                                <Typography variant="h6" sx={{ color: '#ff6b00', fontWeight: 600, mb: 2 }}>
                                    {t('loyalty.profile.recentActivity', 'Последна Активност')}
                                </Typography>
                                <Box className="logs-timeline">
                                    {recentLogs.slice(0, 5).map((log) => {
                                        const hookahTypeName = log.hookah_type
                                            ? (i18n.language === 'bg' && log.hookah_type.name_bg ? log.hookah_type.name_bg : log.hookah_type.name)
                                            : 'Standard';
                                        const pointsEarned = log.points_earned || (log.increment_amount || 1);

                                        return (
                                            <Box key={log.id} className="log-entry">
                                                <Box className="log-indicator" />
                                                <Box className="log-content">
                                                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
                                                        {`+${log.increment_amount} ${hookahTypeName}`}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#4caf50', display: 'block', fontWeight: 600 }}>
                                                        +{Number(pointsEarned).toFixed(1).replace(/\.0$/, '')} {t('loyalty.profile.points', 'точки')}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
                                                        {new Date(log.created_at).toLocaleDateString(i18n.language === 'bg' ? 'bg-BG' : 'en-US', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Image Preview Dialog */}
            <Dialog
                open={!!imagePreview}
                onClose={() => setImagePreview(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: 'rgba(30, 30, 30, 0.98)',
                        borderRadius: '16px'
                    }
                }}
            >
                <DialogTitle sx={{
                    color: '#ff6b00',
                    fontWeight: 600,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(255, 107, 0, 0.2)'
                }}>
                    {imagePreview?.name}
                    <IconButton
                        onClick={() => setImagePreview(null)}
                        sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '2rem',
                    bgcolor: '#000'
                }}>
                    <Box
                        component="img"
                        src={imagePreview?.url}
                        alt={imagePreview?.name}
                        sx={{
                            maxWidth: '100%',
                            maxHeight: '70vh',
                            objectFit: 'contain'
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* Promoted Shop Modal */}
            <PromotedShopModal
                open={showPromotedModal}
                onClose={() => setShowPromotedModal(false)}
            />
        </Box>
    );
}

export default LoyaltyProfile;
